import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GridOptions, GridSizeChangedEvent, RowClickedEvent } from 'ag-grid';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/operator/finally';
import { ConfigurationService } from '../../../services/administration/configuration.service';
import { IConfiguration, IConfigurationRequest } from 'app/core/interfaces/configuration.interface';
import { IConfigurationHistoryRow } from 'app/core/interfaces/configuration-history.interface';
import { IQuestionType } from 'app/core/interfaces/question-type.interface';
import { T2LoadingService } from 'app/trip2-theme/components/t2-loading/t2-loading.service';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';

const QUESTION_TYPE = {
  FreeText: 'Free Text',
  Int: 'Int',
  Bool: 'Bool',
  Json: 'JSON',
  SelectionMultiple: 'Selection Multiple',
};

const SUPPORTED_QUESTION_TYPES = [
  QUESTION_TYPE.FreeText,
  QUESTION_TYPE.Int,
  QUESTION_TYPE.Bool,
  QUESTION_TYPE.Json,
  QUESTION_TYPE.SelectionMultiple,
];

declare const JSONEditor: any;

@Component({
  selector: 'app-configuration',
  templateUrl: './configuration.component.html',
  styleUrls: ['./configuration.component.scss'],
})
export class ConfigurationComponent implements OnInit, OnDestroy {
  gridConfig: GridOptions;
  rows: IConfiguration[] = [];
  selected: IConfiguration | null = null;
  draft = '';
  saving = false;
  creating = false;
  showDisabled = false;
  tab = 'details';
  history: IConfigurationHistoryRow[] = [];
  historyGrid: GridOptions;
  options: string[] = [];
  questionTypes: IQuestionType[] = [];
  jsonEditor: any;
  optionsJsonEditor = {
    modes: ['code', 'tree', 'text', 'form', 'view'],
    onChange: () => (this.draft = this.jsonEditor.getText()),
  };
  private jsonEditorHost: HTMLElement | null = null;
  private subscriptions: Subscription[] = [];

  @ViewChild('jsoneditor')
  set jsonEditorElement(element: ElementRef | undefined) {
    const host: HTMLElement | null = element ? element.nativeElement : null;
    if (host === this.jsonEditorHost) {
      return;
    }
    this.destroyJsonEditor();
    if (host) {
      this.jsonEditorHost = host;
      this.jsonEditor = new JSONEditor(host, this.optionsJsonEditor, {});
      this.refreshJsonEditor();
    }
  }

  constructor(
    private configurationService: ConfigurationService,
    private loadingService: T2LoadingService,
    private sweetAlertHelper: SweetAlertHelper
  ) {
    this.gridConfig = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      onRowClicked: (event: RowClickedEvent) => this.selectRow(event),
      onGridReady: () => this.bindRows(),
      onGridSizeChanged: (params: GridSizeChangedEvent) => params.api.sizeColumnsToFit(),
    };
    this.gridConfig.columnDefs = [
      { headerName: 'Description', field: 'description', colId: 'description', width: 220 },
      { headerName: 'Question Type', field: 'questiontype', colId: 'questiontype', width: 150 },
      { headerName: 'Value', field: 'value', colId: 'value', width: 280 },
    ];
    this.historyGrid = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      columnDefs: [
        { headerName: 'Date', field: 'date', width: 110 },
        { headerName: 'Time', field: 'time', width: 80 },
        { headerName: 'Description', field: 'description', width: 400 },
        { headerName: 'User', field: 'user', width: 160 },
      ],
      onGridReady: () => this.bindHistory(),
      onGridSizeChanged: (params: GridSizeChangedEvent) => params.api.sizeColumnsToFit(),
    };
  }

  ngOnInit() {
    this.loadQuestionTypes();
  }

  get typeName(): string {
    const id = this.selected && this.selected.questiontypeid;
    const match = this.questionTypes.find((type) => type.questiontypeid === id);
    return match ? match.description : (this.selected && this.selected.questiontype) || '';
  }

  get isSelectionMultiple(): boolean {
    return this.typeName === QUESTION_TYPE.SelectionMultiple;
  }

  get isJson(): boolean {
    return this.typeName === QUESTION_TYPE.Json;
  }

  get isInt(): boolean {
    return this.typeName === QUESTION_TYPE.Int;
  }

  get isFreeText(): boolean {
    const type = this.typeName;
    return type === QUESTION_TYPE.FreeText || !type;
  }

  get isBool(): boolean {
    return this.typeName === QUESTION_TYPE.Bool;
  }

  get isJsonEmpty(): boolean {
    return this.isJson && !(this.draft || '').trim();
  }

  get isBoolEnabled(): boolean {
    return this.draft === 'true';
  }

  get boolLabel(): string {
    return this.isBoolEnabled ? 'Enabled' : 'Disabled';
  }

  get filledOptions(): string[] {
    return this.options.filter((option) => option && option.trim()).map((option) => option.trim());
  }

  get panelTitle(): string {
    return this.creating ? 'Add Configuration' : 'Edit Configuration - ' + (this.selected ? this.selected.description : '');
  }

  get saveLabel(): string {
    return this.creating ? 'Create' : 'Save';
  }

  get isDetailsTab(): boolean {
    return this.tab === 'details';
  }

  get isHistoryTab(): boolean {
    return this.tab === 'history';
  }

  load() {
    this.subscriptions.push(
      this.configurationService.getActive(this.showDisabled).subscribe({
        next: (response) => this.onRowsLoaded(response),
        error: (error) => {
          this.rows = [];
          this.bindRows();
          this.sweetAlertHelper.captureException(error);
        },
      })
    );
  }

  showInactive(checked: boolean) {
    this.showDisabled = checked;
    this.load();
  }

  startAdd() {
    this.creating = true;
    this.tab = 'details';
    this.history = [];
    const freeText = this.questionTypes.find((type) => type.description === QUESTION_TYPE.FreeText);
    this.selected = {
      description: '',
      questiontypeid: freeText ? freeText.questiontypeid : null,
      questiontype: QUESTION_TYPE.FreeText,
      isactive: true,
    };
    this.draft = '';
    this.options = [];
    this.fitGrid();
  }

  selectRow(event: { data: IConfiguration }) {
    this.creating = false;
    this.tab = 'details';
    this.history = [];
    const selected = event.data;
    this.selected = selected;
    if (selected.isactive == null) {
      selected.isactive = true;
    }
    this.draft = this.displayValue(event.data);
    this.options = this.readOptions(event.data);
    this.refreshJsonEditor();
    this.fitGrid();
  }

  openDetails() {
    this.tab = 'details';
  }

  openHistory() {
    if (this.creating || !this.selected) {
      return;
    }
    this.tab = 'history';
    this.subscriptions.push(
      this.configurationService.getHistory(this.selected.configurationid as number).subscribe({
        next: (rows) => {
          this.history = this.asArray(rows);
          this.bindHistory();
        },
        error: (error) => {
          this.history = [];
          this.bindHistory();
          this.sweetAlertHelper.captureException(error);
        },
      })
    );
  }

  cancel() {
    this.creating = false;
    this.selected = null;
    this.draft = '';
    this.options = [];
    this.fitGrid();
  }

  save() {
    if (!this.canSave()) {
      return;
    }
    const created = this.creating;
    const isActive = (this.selected as IConfiguration).isactive !== false;
    this.saving = true;
    this.loadingService.show();
    this.subscriptions.push(
      this.saveRequest(this.buildPayload())
        .finally(() => {
          this.saving = false;
          this.loadingService.hiden();
        })
        .subscribe({
          next: (saved) => this.onSaved(saved, created, isActive),
          error: (error) => this.sweetAlertHelper.captureException(error),
        })
    );
  }

  changeQuestionType(typeId: number) {
    if (!this.selected) {
      return;
    }
    this.selected.questiontypeid = typeId;
    const type = this.typeName;
    this.selected.questiontype = type;
    if (type === QUESTION_TYPE.Bool) {
      this.draft = this.draft === 'true' ? 'true' : 'false';
    } else if (type === QUESTION_TYPE.SelectionMultiple && this.filledOptions.indexOf(this.draft) < 0) {
      const filled = this.filledOptions;
      this.draft = filled.length ? filled[0] : '';
    }
  }

  setBool(checked: boolean) {
    this.draft = checked ? 'true' : 'false';
  }

  trackOption(index: number): number {
    return index;
  }

  addOption() {
    this.options.push('');
  }

  setOption(index: number, value: string) {
    this.options[index] = value;
    if (this.filledOptions.indexOf(this.draft) < 0) {
      const filled = this.filledOptions;
      this.draft = filled.length ? filled[0] : '';
    }
  }

  removeOption(index: number) {
    this.options.splice(index, 1);
    if (this.filledOptions.indexOf(this.draft) < 0) {
      const filled = this.filledOptions;
      this.draft = filled.length ? filled[0] : '';
    }
  }

  resizeJsonEditor() {
    if (this.jsonEditor && this.jsonEditor.resize) {
      this.jsonEditor.resize();
    }
  }

  private loadQuestionTypes() {
    this.subscriptions.push(
      this.configurationService.getQuestionTypes().subscribe({
        next: (types) => {
          this.questionTypes = this.toSupportedQuestionTypes(types);
          this.load();
        },
        error: (error) => {
          this.questionTypes = [];
          this.sweetAlertHelper.captureException(error);
          this.load();
        },
      })
    );
  }

  private asArray<T>(value: T[]): T[] {
    return Array.isArray(value) ? value : [];
  }

  private toSupportedQuestionTypes(types: IQuestionType[]): IQuestionType[] {
    return this.asArray(types).filter((type) => SUPPORTED_QUESTION_TYPES.indexOf(type.description) >= 0);
  }

  private toVisibleRows(rows: IConfiguration[]): IConfiguration[] {
    return this.asArray(rows).filter((row) => this.showDisabled || row.isactive !== false);
  }

  private onRowsLoaded(response: IConfiguration[]) {
    this.rows = this.toVisibleRows(response);
    this.bindRows();
    if (this.creating) {
      return;
    }
    if (this.selected && this.selected.configurationid) {
      const selectedId = this.selected.configurationid;
      const current = this.rows.find((row) => row.configurationid === selectedId);
      this.selected = current || null;
      if (current) {
        this.draft = this.displayValue(current);
        this.options = this.readOptions(current);
        this.refreshJsonEditor();
      }
    }
  }

  private displayValue(row: IConfiguration): string {
    return row && row.value ? row.value : '';
  }

  private readOptions(row: IConfiguration): string[] {
    if (!row || !row.options) {
      return [];
    }
    try {
      const parsed = JSON.parse(row.options);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  private canSave(): boolean {
    if (!this.selected || this.saving) {
      return false;
    }
    if (this.creating && !this.trimmedDescription) {
      return false;
    }
    if (this.isJson) {
      this.syncJsonDraft();
      if (!this.isValidJson(this.draft)) {
        this.sweetAlertHelper.createCustomAlert({ title: 'Error', type: 'error', text: 'Value is not valid JSON.' });
        return false;
      }
    }
    return true;
  }

  private get trimmedDescription(): string {
    return ((this.selected && this.selected.description) || '').trim();
  }

  private syncJsonDraft() {
    if (this.jsonEditor) {
      this.draft = this.jsonEditor.getText();
    }
  }

  private buildPayload(): IConfigurationRequest {
    const selected = this.selected as IConfiguration;
    const payload: IConfigurationRequest = {
      value: this.draft == null ? '' : String(this.draft),
      questiontypeid: selected.questiontypeid,
      options: this.isSelectionMultiple ? JSON.stringify(this.filledOptions) : null,
      isactive: selected.isactive !== false,
    };
    if (this.creating) {
      payload.description = this.trimmedDescription;
    }
    return payload;
  }

  private saveRequest(payload: IConfigurationRequest): Observable<IConfiguration> {
    return this.creating
      ? this.configurationService.create(payload)
      : this.configurationService.update((this.selected as IConfiguration).configurationid as number, payload);
  }

  private onSaved(saved: IConfiguration, created: boolean, isActive: boolean) {
    this.creating = false;
    this.sweetAlertHelper.createCustomAlert({
      title: 'Success',
      type: 'success',
      text: created ? 'Configuration created successfully.' : 'Configuration saved successfully.',
    });
    if (!isActive) {
      this.cancel();
    } else if (saved && saved.configurationid) {
      this.selected = saved;
    }
    this.load();
  }

  private isValidJson(text: string): boolean {
    try {
      JSON.parse(text);
      return true;
    } catch (e) {
      return false;
    }
  }

  private refreshJsonEditor() {
    if (!this.jsonEditor) {
      return;
    }
    try {
      this.jsonEditor.update(JSON.parse(this.draft));
    } catch (e) {
      this.jsonEditor.setMode('text');
      this.jsonEditor.setText(this.draft || '');
    }
  }

  private destroyJsonEditor() {
    if (this.jsonEditor) {
      this.jsonEditor.destroy();
    }
    this.jsonEditor = null;
    this.jsonEditorHost = null;
  }

  private bindRows() {
    if (!this.gridConfig.api) {
      return;
    }
    const visible = this.rows.filter((row) => this.showDisabled || row.isactive !== false);
    this.gridConfig.api.setRowData(visible);
    this.fitGrid();
  }

  private bindHistory() {
    const api = this.historyGrid && this.historyGrid.api;
    if (!api) {
      return;
    }
    api.setRowData(this.history || []);
    api.sizeColumnsToFit();
  }

  private fitGrid() {
    const api = this.gridConfig && this.gridConfig.api;
    if (!api) {
      return;
    }
    api.sizeColumnsToFit();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.destroyJsonEditor();
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/administration/configuration/configuration.component.ts